import { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import usePermission from "../../../config/permissions";
import axios from "axios";
import { BASE_URL } from "../../../config/axios";
import { InfoIcon, Office, SuccessIcon } from "../../AllSvg";
import { handleErrorToast } from "../../CustomFunctions";
import { getResultCardStyle } from "../../../style/CustomFunctionForCss";

// ── helpers ───────────────────────────────────────────────────────────────────
const MONTHS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

// const currentYear = new Date().getFullYear();
// const currentMonth = new Date().getMonth() + 1;

// // "YYYY-MM" string for <input type="month"> default value & max
// const toMonthInputValue = (year, month) =>
//     `${year}-${String(month).padStart(2, "0")}`;

// const todayMonthValue = toMonthInputValue(currentYear, currentMonth);

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

// "YYYY-MM" string for <input type="month"> default value & max
const toMonthInputValue = (year, month) =>
    `${year}-${String(month).padStart(2, "0")}`;

const todayMonthValue = toMonthInputValue(currentYear, currentMonth);

// ── Civilmantra: calendar month till 24 May 2026, 25–24 salary cycle from 25 May 2026 onward ──
const CIVILMANTRA_ID = "025d0b52-e892-4868-a3bb-6b70dcdffb4d";
const SALARY_CYCLE_CHANGE_DATE = new Date(2026, 5, 25); // 25 May 2026

const getCycleMonthYear = (date) => {
    let month = date.getMonth() + 1;
    let year = date.getFullYear();
    const isCycleActive = date >= SALARY_CYCLE_CHANGE_DATE;
    if (isCycleActive && date.getDate() >= 25) {
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
    }
    return { month, year };
};


const getDefaultMonthYear = (companyId) => {
    if (companyId === CIVILMANTRA_ID) {
        return getCycleMonthYear(new Date());
    }
    return { month: currentMonth, year: currentYear };
};

const getMaxMonthValue = (companyId) => {
    const { month, year } = getDefaultMonthYear(companyId);
    return toMonthInputValue(year, month);
};

const formatDateTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
};

// ── result summary card ───────────────────────────────────────────────────────
const ResultCard = ({ result, params }) => {
    const [showAffected, setShowAffected] = useState(false);
    const [showUnaffected, setShowUnaffected] = useState(false);

    const { summary = {}, affected = {}, unaffected = {}, log_meta = {}, message } = result;
    const affectedList = Object.entries(affected);    // [ [empId, [dates]], ... ]
    const unaffectedList = Object.entries(unaffected);  // [ [empId, []], ... ]

    const monthLabel = MONTHS.find((m) => m.value === Number(params.month))?.label;

    return (
        <div
            className="sandwich-result-card"
            style={getResultCardStyle({
                background: "#fff",
                borderColor: "#c0dd97",
                leftBorderColor: "#5aad1e",
                borderRadius: "10px",
            })}
        >

            {/* ── header ── */}
            <div className="sandwich-result-header">
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {/* <span className="sandwich-result-icon">✓</span> */}
                    <SuccessIcon
                        size={30}
                        bgColor="#e8f5e9"
                        tickColor="#2e7d32"
                    />
                    <div>
                        <div className="font-weight500" style={{ fontSize: "15px" }}>
                            {message || "Executed Successfully"}
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--color-text-secondary,#888)", marginTop: "3px" }}>
                            {monthLabel} {params.year}
                            &nbsp;·&nbsp;Run #{log_meta.run_count}
                            &nbsp;·&nbsp;{formatDateTime(log_meta.last_run_at)}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── 3 stat boxes ── */}
            <div className="sandwich-result-stats">
                <div className="sandwich-stat-box">
                    <span className="sandwich-stat-label">Total Employees Checked</span>
                    <span className="sandwich-stat-value">{summary.total_employees ?? "—"}</span>
                    <span className="sandwich-stat-hint">Employees scanned for sandwich pattern</span>
                </div>
                <div className="sandwich-stat-box applied">
                    <span className="sandwich-stat-label">Sandwich Applied</span>
                    <span className="sandwich-stat-value" style={{ color: "#3b6d11" }}>
                        {summary.affected_count ?? "—"}
                    </span>
                    <span className="sandwich-stat-hint">
                        {summary.affected_count > 0
                            ? "Extra leave days deducted for weekend sandwich"
                            : "No sandwich pattern found this month"}
                    </span>
                </div>
                <div className="sandwich-stat-box not-applied">
                    <span className="sandwich-stat-label">Not Affected</span>
                    <span className="sandwich-stat-value" style={{ color: "#854f0b" }}>
                        {summary.unaffected_count ?? "—"}
                    </span>
                    <span className="sandwich-stat-hint">No qualifying leave pattern detected</span>
                </div>
            </div>

            {/* ── drill-down toggles ── */}
            <div className="sandwich-result-drilldown">

                {/* affected employees */}
                {affectedList.length > 0 && (
                    <div className="sandwich-drill-section">
                        <button
                            className="sandwich-drill-toggle"
                            onClick={() => setShowAffected((p) => !p)}
                        >
                            <span className="sandwich-chip applied" style={{ marginRight: "8px" }}>
                                {affectedList.length} Affected
                            </span>
                            <span style={{ fontSize: "12px", color: "var(--color-text-secondary,#888)" }}>
                                Weekend / holiday days auto-deducted
                            </span>
                            <span className={`sandwich-expand-icon ${showAffected ? "open" : ""}`} style={{ marginLeft: "auto" }}>
                                <svg
                                    width="16" height="16" viewBox="0 0 16 16" fill="none"
                                    style={{
                                        marginLeft: "auto",
                                        flexShrink: 0,
                                        color: "var(--color-text-secondary, #888)",
                                        transition: "transform 0.2s ease",
                                        transform: showAffected ? "rotate(180deg)" : "rotate(0deg)",
                                    }}
                                >
                                    <path d="M3 6L8 11L13 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </button>

                        {showAffected && (
                            <table className="table-css table-table-css" style={{ marginTop: "8px" }}>
                                <thead>
                                    <tr className="custom-table-head-tr">
                                        <th className="align-left">S.No</th>
                                        <th className="align-center">Employee ID</th>
                                        <th className="align-center">Dates Deducted</th>
                                        <th className="align-center">Days Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {affectedList.map(([empId, dates], idx) => (
                                        <>
                                            <tr className="tr-border-bottom" key={`asep-${idx}`}><td colSpan="4"></td></tr>
                                            <tr className="custom-table-head-td custom-custom-table-head-td" key={`arow-${idx}`}>
                                                <td className="align-left">{idx + 1}</td>
                                                <td className="align-center font-weight500">{empId}</td>
                                                <td className="align-center">
                                                    {dates.length > 0 ? (
                                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", justifyContent: "center" }}>
                                                            {dates.map((d) => (
                                                                <span key={d} className="sandwich-date-tag">{d}</span>
                                                            ))}
                                                        </div>
                                                    ) : "—"}
                                                </td>
                                                <td className="align-center">
                                                    <span className="sandwich-chip applied">{dates.length} day{dates.length !== 1 ? "s" : ""}</span>
                                                </td>
                                            </tr>
                                        </>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* unaffected employees */}
                {unaffectedList.length > 0 && (
                    <div className="sandwich-drill-section">
                        <button
                            className="sandwich-drill-toggle"
                            onClick={() => setShowUnaffected((p) => !p)}
                        >
                            <span className="sandwich-chip not-applied" style={{ marginRight: "8px" }}>
                                {unaffectedList.length} Not Affected
                            </span>
                            <span style={{ fontSize: "12px", color: "var(--color-text-secondary,#888)" }}>
                                No sandwich pattern found
                            </span>
                            <span className={`sandwich-expand-icon ${showUnaffected ? "open" : ""}`} style={{ marginLeft: "auto" }}>
                                <svg
                                    width="16" height="16" viewBox="0 0 16 16" fill="none"
                                    style={{
                                        marginLeft: "auto",
                                        flexShrink: 0,
                                        color: "var(--color-text-secondary, #888)",
                                        transition: "transform 0.2s ease",
                                        transform: showUnaffected ? "rotate(180deg)" : "rotate(0deg)",
                                    }}
                                >
                                    <path d="M3 6L8 11L13 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </button>

                        {showUnaffected && (
                            <table className="table-css table-table-css" style={{ marginTop: "8px" }}>
                                <thead>
                                    <tr className="custom-table-head-tr">
                                        <th className="align-left">S.No</th>
                                        <th className="align-center">Employee ID</th>
                                        <th className="align-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unaffectedList.map(([empId], idx) => (
                                        <>
                                            <tr className="tr-border-bottom" key={`usep-${idx}`}><td colSpan="3"></td></tr>
                                            <tr className="custom-table-head-td custom-custom-table-head-td" key={`urow-${idx}`}>
                                                <td className="align-left">{idx + 1}</td>
                                                <td className="align-center font-weight500">{empId}</td>
                                                <td className="align-center">
                                                    <span className="sandwich-chip not-applied">No Pattern Found</span>
                                                </td>
                                            </tr>
                                        </>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

// ── main component ────────────────────────────────────────────────────────────


const ExecuteSandwichRule = () => {
    const { SUB_COMPANIES, SUPER_ADMIN } = usePermission();


    // const [monthValue, setMonthValue] = useState(todayMonthValue);
    // const [year, setYear] = useState(currentYear);
    // const [month, setMonth] = useState(currentMonth);

    // const [subCompany, setSubCompany] = useState(
    //     sessionStorage.getItem("company_id") || ""
    // );
    const initialCompany = sessionStorage.getItem("company_id") || "";
    const initialMonthYear = getDefaultMonthYear(initialCompany);

    const [monthValue, setMonthValue] = useState(
        toMonthInputValue(initialMonthYear.year, initialMonthYear.month)
    );
    const [year, setYear] = useState(initialMonthYear.year);
    const [month, setMonth] = useState(initialMonthYear.month);

    const [subCompany, setSubCompany] = useState(initialCompany);

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState({});

    // ── month picker handler ───────────────────────────────────────────────
    const handleMonthChange = (e) => {
        const value = e.target.value; // "YYYY-MM"
        if (value) {
            const [y, m] = value.split("-");
            setMonthValue(value);
            setYear(Number(y));
            setMonth(Number(m.replace(/^0+/, ""))); // strip leading zero → "05" → 5
        }
        setErrors((prev) => ({ ...prev, month: "" }));
        setResult(null);
    };

    // const handleSubCompanyChange = (e) => {
    //     setSubCompany(e.target.value);
    //     setErrors((prev) => ({ ...prev, sub_company: "" }));
    //     setResult(null);
    // };

    const handleSubCompanyChange = (e) => {
        const newCompany = e.target.value;
        setSubCompany(newCompany);

        // company change hote hi month field bhi us company ke correct default pe reset ho
        const defaultMonthYear = getDefaultMonthYear(newCompany);
        setMonthValue(toMonthInputValue(defaultMonthYear.year, defaultMonthYear.month));
        setYear(defaultMonthYear.year);
        setMonth(defaultMonthYear.month);

        setErrors((prev) => ({ ...prev, sub_company: "" }));
        setResult(null);
    };

    // ── validation ────────────────────────────────────────────────────────
    const validate = () => {
        const newErrors = {};

        if (!subCompany || subCompany === "null")
            newErrors.sub_company = " Sub_company is required !";
        if (!monthValue)
            newErrors.month = " Month is required !";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ── execute ───────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        setResult(null);
        const loadingToastId = toast.loading("Loading: Please wait...");
        try {
            const empCode = sessionStorage.getItem("emp_code");
            const payload = {
                sub_company: subCompany,
                year,
                month,
                created_by: empCode,
                updated_by: empCode,
            };

            const res = await axios.post(
                `${BASE_URL}/wfm/sandwich-rule-execute/`,
                payload
            );

            if (res.status === 200 || res.status === 201) {
                setResult(res.data);
                toast.success("Sandwich rule executed successfully!");
            } else {
                console.error("Something went wrong: " + res.statusText);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    // ── reset ─────────────────────────────────────────────────────────────
    // const handleReset = () => {
    //     setSubCompany(sessionStorage.getItem("company_id") || "");
    //     setMonthValue(todayMonthValue);
    //     setYear(currentYear);
    //     setMonth(currentMonth);
    //     setResult(null);
    //     setErrors({});
    // };

    const handleReset = () => {
        const resetCompany = sessionStorage.getItem("company_id") || "";
        const defaultMonthYear = getDefaultMonthYear(resetCompany);

        setSubCompany(resetCompany);
        setMonthValue(toMonthInputValue(defaultMonthYear.year, defaultMonthYear.month));
        setYear(defaultMonthYear.year);
        setMonth(defaultMonthYear.month);
        setResult(null);
        setErrors({});
    };

    const selectedCompanyName = SUB_COMPANIES?.find((c) => c.id === subCompany)?.title;
    const selectedMonthLabel = MONTHS.find((m) => m.value === month)?.label;

    return (
        <>
            <div style={{ margin: "10px" }} className="content-tabs">

                {/* info banner */}
                <div className="sandwich-info-banner">
                    <span>
                        <InfoIcon />
                    </span>
                    <span>
                        The sandwich rule automatically deducts weekends and holidays that fall
                        between consecutive leave/absent days. Select the period and company, then click
                        <strong> Execute</strong> to apply.
                    </span>
                </div>

                {/* form card */}
                <div className="table-css-white-background" style={{ marginBottom: "20px", padding: '10px' }}>


                    <div className="sandwichrule-field-parent-cont field-parent-cont" style={{ gap: "20px", marginBottom: '0px' }}>

                        {/* Sub Company */}

                        <div className="sandwichrule-field-cont-div field-cont-div">
                            <Office />
                            <select
                                value={subCompany}
                                onChange={handleSubCompanyChange}
                                className={`sandwichrule-attendance-input-field attendance-input-field field-width-15vw width-15vw date-field ${errors.sub_company ? "error-border" : ""}`}
                            >
                                <option value="">Select Sub Company</option>
                                {SUB_COMPANIES?.map((c) => (
                                    <option key={c.id} value={c.id}>{c.title}</option>
                                ))}
                            </select>
                            {errors.sub_company && (
                                <span className="error-message font-size-text ">
                                    {errors.sub_company}
                                </span>
                            )}
                            <hr className="field-cont-hr" />
                        </div>


                        {/* Month picker — replaces separate Year + Month dropdowns */}

                        <div className="sandwichrule-field-cont-div field-cont-div">

                            {/* <input
                                type="month"
                                className={`sandwichrule-attendance-input-field attendance-input-field calender-input field-width-10vw width-10vw ${errors.month ? "error-border" : ""}`}
                                value={monthValue}
                                max={todayMonthValue}
                                onChange={handleMonthChange}
                            /> */}

                            <input
                                type="month"
                                className={`sandwichrule-attendance-input-field attendance-input-field calender-input field-width-10vw width-10vw ${errors.month ? "error-border" : ""}`}
                                value={monthValue}
                                max={getMaxMonthValue(subCompany)}
                                onChange={handleMonthChange}
                            />
                            {errors.month && (
                                <span className="error-message font-size-text ">
                                    {errors.month}
                                </span>
                            )}
                            <hr className="field-cont-hr" />
                        </div>

                        <div style={{ display: "flex", gap: "10px", marginLeft: "auto" }}>
                            <button
                                className="model-button modal-model-button model-button-cancel font-weight500"
                                onClick={handleReset}
                                disabled={loading}
                            >
                                Reset
                            </button>

                            <button
                                className="model-button modal-model-button model-button-leave font-weight500"
                                onClick={handleSubmit}
                                disabled={loading}
                                style={{ minWidth: "180px", justifyContent: "center" }}
                            >
                                {loading ? (
                                    <>
                                        <div
                                            className="sandwich-btn-spinner"
                                            role="status"
                                            aria-label="Executing…"
                                        />
                                        Executing…
                                    </>
                                ) : (
                                    <>▶ Execute Sandwich Rule</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* preview pill + action buttons */}
                    {/* <div className="sandwich-action-row">
 
                    </div> */}
                </div>

                {/* result card */}
                {result && (
                    <ResultCard
                        result={result}
                        params={{ month, year }}
                    />
                )}

            </div>

            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
};

export default ExecuteSandwichRule;
